import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';

// Backend API URL - automatically detects Vercel deployment or uses localhost for development
const getApiUrl = () => {
  // In production on Vercel, API routes are on the same domain at /api
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  // In development, use localhost backend or environment variable
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const API_URL = getApiUrl();

const RATE_LIMIT_KEY = 'cpt_form_submissions';
const LIMIT_PER_MINUTE = 3;
const LIMIT_PER_30_MIN = 5;

const getRateLimitStatus = () => {
  const now = Date.now();
  const oneMinuteAgo = now - 60 * 1000;
  const thirtyMinutesAgo = now - 30 * 60 * 1000;

  const stored = localStorage.getItem(RATE_LIMIT_KEY);
  const submissions = stored ? JSON.parse(stored) : [];

  // Clean up old submissions (older than 30 min)
  const validSubmissions = submissions.filter(time => time > thirtyMinutesAgo);

  const lastMinuteCount = validSubmissions.filter(time => time > oneMinuteAgo).length;
  const last30MinCount = validSubmissions.length;

  return {
    canSubmit: lastMinuteCount < LIMIT_PER_MINUTE && last30MinCount < LIMIT_PER_30_MIN,
    lastMinuteCount,
    last30MinCount,
    submissions: validSubmissions
  };
};

const recordSubmission = () => {
  const { submissions } = getRateLimitStatus();
  submissions.push(Date.now());
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(submissions));
};

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: ''
  });
  const sectionRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    // Check rate limit
    const { canSubmit, lastMinuteCount, last30MinCount } = getRateLimitStatus();
    if (!canSubmit) {
      if (lastMinuteCount >= LIMIT_PER_MINUTE) {
        setRateLimitError('Trop de soumissions. Veuillez attendre une minute avant de réessayer.');
      } else if (last30MinCount >= LIMIT_PER_30_MIN) {
        setRateLimitError('Limite de soumissions atteinte. Veuillez attendre 30 minutes avant de réessayer.');
      }
      return;
    }

    setRateLimitError(null);
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send form data to backend API (credentials are secure on the server)
      // On Vercel: /api/contact, On localhost: http://localhost:5000/api/contact
      const endpoint = API_URL ? `${API_URL}/api/contact` : '/api/contact';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || '',
          phone: formData.phone || '',
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send email');
      }

      // Record successful submission for rate limiting
      recordSubmission();

      setSubmitStatus('success');
      setFormData({ name: '', company: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" id="contact" ref={sectionRef}>
      <div className="section-container">
        <div className="contact-grid">
          <div className={`contact-info ${isVisible ? 'visible' : ''}`}>
            <h2>Contactez<br />CPT Express</h2>
            <p>
              Prêt à optimiser vos opérations de transport? Notre équipe est disponible 
              pour discuter de vos besoins.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div className="contact-item-text">
                  <strong>Courriel</strong>
                  info@cptexpress.ca
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div className="contact-item-text">
                  <strong>Téléphone</strong>
                  (514)-885-7516
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div className="contact-item-text">
                  <strong>Localisation</strong>
                  Montréal, Québec
                </div>
              </div>
            </div>
            <div className="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178788.68197058045!2d-73.87439595!3d45.5592149!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a541c64b70d%3A0x654e3138211fefef!2sMontreal%2C%20QC!5e0!3m2!1sen!2sca!4v1699900000000!5m2!1sen!2sca"
                loading="lazy"
                title="CPT Express Location"
              ></iframe>
            </div>
          </div>
          <div className={`contact-form-wrapper ${isVisible ? 'visible' : ''}`}>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Nom complet</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Compagnie</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Nom de l'entreprise"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="(514)-885-7516"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Courriel</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="votre@courriel.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group full">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Comment pouvons-nous vous aider?"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
              </div>
              <div className="form-submit">
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                  {!isSubmitting && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  )}
                </button>
                {submitStatus === 'success' && (
                  <p className="submit-message success">
                    Merci pour votre message! Nous vous contacterons bientot(verifiez votre dossier spam).
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="submit-message error">
                    Une erreur s'est produite. Veuillez reessayer ou nous contacter directement.
                  </p>
                )}
                {rateLimitError && (
                  <p className="submit-message error">
                    {rateLimitError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
